import { assign, AssignAction, GuardMeta, Interpreter, MachineOptions, StateMachine } from 'xstate'

import { FieldDataValue } from 'embed/constants'

export interface GeneralizedFollowupQuestion {
  triggeredQuestionUuid: string
  triggeringValue: string | string[]
}

export interface QuestionMachineContext {
  currentStep: number
  totalNumberOfSteps: number
  followupStep: number
}

export enum QuestionMachineEventType {
  ANSWER = 'ANSWER',
}

export enum QuestionMachineGuard {
  EventValueIsCondValue = 'eventValueIsCondValue',
}

export enum QuestionMachineAction {
  IncrementFollowupQuestionStep = 'incrementFollowupQuestionStep',
  IncrementCurrentStep = 'incrementCurrentStep',
}

export interface QuestionMachineEvent {
  type: QuestionMachineEventType
  value?: FieldDataValue
}

export enum FinalStateNodeNames {
  Complete = 'complete',
}

export const finalQuestionMachineStates = {
  // typecast as const due to https://github.com/davidkpiano/xstate/issues/965
  [FinalStateNodeNames.Complete]: { type: 'final' as const },
}

export interface QuestionMachineTransitionCondition {
  type: QuestionMachineGuard
  value: string | string[]
}

export interface QuestionMachineTransitionDefinition {
  target: string
  cond?: QuestionMachineTransitionCondition
  actions?: QuestionMachineAction
}

export interface NonCompleteQuestionMachineState {
  [key: string]: {
    on: {
      [key in QuestionMachineEventType]: QuestionMachineTransitionDefinition[]
    }
  }
}

export type QuestionMachineState = typeof finalQuestionMachineStates | NonCompleteQuestionMachineState

export interface QuestionMachineGuardMeta extends GuardMeta<QuestionMachineContext, QuestionMachineEvent> {
  cond: QuestionMachineTransitionCondition
}

export interface QuestionMachineOptions extends Partial<MachineOptions<QuestionMachineContext, QuestionMachineEvent>> {
  guards: {
    [key in QuestionMachineGuard]: (
      context: QuestionMachineContext,
      event: QuestionMachineEvent,
      meta: QuestionMachineGuardMeta,
    ) => boolean
  }
  actions: {
    [key in QuestionMachineAction]: AssignAction<QuestionMachineContext, QuestionMachineEvent>
  }
}

export const questionMachineId = 'question'

export interface QuestionMachineStateSchema {
  states: QuestionMachineState
}

export type QuestionMachineConfig = {
  id: typeof questionMachineId
  initial: keyof NonCompleteQuestionMachineState
  context: QuestionMachineContext
} & QuestionMachineStateSchema

const arraysAreEqual = <T extends unknown>(array1: T[], array2: T[]): boolean =>
  array1.length === array2.length && array1.every((el1) => array2.some((el2) => el1 === el2))

// I couldn't avoid an `any` here because the official definition of the StateSchema interface seems incorrect:
// https://github.com/davidkpiano/xstate/blob/560e28fbb208967d29be0244fe507a021c73f242/packages/core/src/types.ts#L1055
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QuestionStateMachine = StateMachine<QuestionMachineContext, any, QuestionMachineEvent>

// I'm following the official type annotation here:
// https://github.com/davidkpiano/xstate/blob/17a44f1fcfb42f74e6424a286bbfb7814c0e0a1e/packages/xstate-react/src/index.ts#L39
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QuestionStateMachineInterpreter = Interpreter<QuestionMachineContext, any, QuestionMachineEvent>['send']

export type QuestionMachineResponsePath = { currentState: string; value: FieldDataValue }[]

export interface QuestionMachineResponsePathWithSteps {
  responsePath: QuestionMachineResponsePath
  stepMap: {
    [key: string]: {
      currentStep: QuestionMachineContext['currentStep']
      followupStep: QuestionMachineContext['followupStep']
    }
  }
  isComplete: boolean
}

export const questionMachineOptions: QuestionMachineOptions = {
  guards: {
    [QuestionMachineGuard.EventValueIsCondValue]: (_, event, { cond }) =>
      Array.isArray(event.value) && Array.isArray(cond.value)
        ? arraysAreEqual(cond.value, event.value)
        : event.value === cond.value,
  },
  actions: {
    [QuestionMachineAction.IncrementFollowupQuestionStep]: assign<QuestionMachineContext, QuestionMachineEvent>({
      followupStep: ({ followupStep }) => followupStep + 1,
    }),
    [QuestionMachineAction.IncrementCurrentStep]: assign<QuestionMachineContext, QuestionMachineEvent>({
      currentStep: ({ currentStep }) => currentStep + 1,
      followupStep: 0,
    }),
  },
}
